import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router/router'
import useUserStore from '@/store/modules/user'
import { addSentryBreadcrumb, captureSentryException } from '@/plugins/sentry'
import { getErrorI18nKey } from '@/utils/errorCodeMap'

const service = axios.create({
  baseURL: import.meta.env.BASE_URL,
  timeout: 30000
})

/**
 * 根据错误码获取错误消息
 * 优先使用错误码映射的多语言文案，如果没有映射则使用后端返回的消息
 * @param code 错误码
 * @param fallbackMsg 后端返回的原始消息（msg/message/debugMsg）
 * @returns 错误消息
 */
const getErrorMessage = (code: number | string | undefined, fallbackMsg: string | undefined): string => {
  if (code && typeof code === 'number') {
    const i18nKey = getErrorI18nKey(code)
    if (i18nKey) {
      return window.$t(i18nKey) as string
    }
  }
  return fallbackMsg || window.$t('serviceUnavailable') as string
}

/**
 * @description 请求拦截器
 */
// const globalStore = GlobalStore()
service.interceptors.request.use(
  (config) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
    // token 存在 才添加
    if (JSON.stringify(userInfo) !== '{}') {
      config.headers.Token = userInfo?.token || ''
      config.headers.userCode = userInfo?.userCode
      config.headers.language = localStorage.getItem('language') ?? 'es'
    }

    addSentryBreadcrumb({
      category: 'http',
      message: `${String(config.method || 'get').toUpperCase()} ${String(config.url || '')}`,
      level: 'info',
      data: {
        url: config.url,
        method: config.method,
        params: config.params
      }
    })
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * @description 响应拦截器
 */
service.interceptors.response.use(
  (response) => {
    const res = response.data
    if (res instanceof Blob) {
      // 流文件
      return res
    }

    if (res.code !== 10000 && response.config?.headers?.hideMsg !== 'true') {
      const errorMessage = getErrorMessage(res.code, res.msg || res.message)
      ElMessage({
        message: errorMessage,
        type: 'error',
        duration: 5 * 1000
      })

      // 业务错误默认只留 breadcrumb，避免把"可预期的 4xx/表单校验"全打成 error
      addSentryBreadcrumb({
        category: 'http',
        message: `API error code=${String(res.code)} url=${String(response.config?.url || '')}`,
        level: 'warning',
        data: {
          url: response.config?.url,
          method: response.config?.method,
          code: res.code
        }
      })

      return Promise.reject(new Error(errorMessage))
    } else {
      return res
    }
  },
  (error) => {
    const status = error?.response?.status as number | undefined
    const url = error?.config?.url as string | undefined
    const method = error?.config?.method as string | undefined

    addSentryBreadcrumb({
      category: 'http',
      message: `HTTP ${String(status || 'ERR')} ${String(method || '').toUpperCase()} ${String(
        url || ''
      )}`,
      level: status && status >= 500 ? 'error' : 'warning',
      data: {
        url,
        method,
        status
      }
    })

    // 默认策略：只自动上报网络错误/5xx，避免 400 噪声
    if (!status || status >= 500) {
      captureSentryException(error, {
        url,
        method,
        status
      })
    }

    if (error.response?.status === 401) {
      handle401Error()
    } else {
      const errorData = error?.response?.data
      const errorCode = errorData?.code
      const fallbackMsg = errorData?.debugMsg || errorData?.msg || errorData?.message
      const errorMessage = getErrorMessage(errorCode, fallbackMsg)
      ElMessage({
        message: errorMessage,
        type: 'error',
        duration: 2 * 1000
      })
    }
    return Promise.reject(error)
  }
)

const handle401Error = () => {
  const has401Error = localStorage.getItem('hasError401') === '1'
  localStorage.setItem('hasError401', '1')
  if (!has401Error) {
    ElMessage({
      message: window.$t('loginExpired'),
      type: 'error',
      duration: 2 * 1000
    })
    useUserStore().logout()
    router.replace('/login')
  }
}

export interface IResponse<T = any> {
  success: boolean
  message: string | null
  code: number | string
  data: T
}

/**
 * 规范请求返回体参数
 *
 * @param {AxiosRequestConfig} config - The configuration for the request.
 * @return {Promise<IResponse<T>>} A promise that resolves with the response model.
 */
function request<T = any>(config: AxiosRequestConfig): Promise<IResponse<T>> {
  return new Promise((resolve, reject) => {
    try {
      service<IResponse<T>>(config)
        .then((res: AxiosResponse['data']) => {
          resolve(res as IResponse<T>)
        })
        .catch((err) => {
          reject(err)
        })
    } catch (err) {
      return Promise.reject(err)
    }
  })
}

export default request
